import { AppleCard } from "@/components/ui/apple-card";
import { Badge } from "@/components/ui/badge";
import { Share2, MapPin, Pencil, ChevronDown, ChevronUp, Plus, ImageIcon, FileText, Bell, Eye } from "lucide-react";
import { ShareDialog } from "@/components/shared/ShareDialog";
import { cn, getStatusColor } from "@/lib/utils";
import { AppleButton } from "@/components/ui/apple-button";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AssetRecordsList } from "./AssetRecordsList";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AssetRecordForm } from "@/components/forms/AssetRecordForm";
import { useNavigate } from "react-router-dom";

interface AssetCardProps {
  id: number;
  name: string;
  imageUrl?: string;
  isShared?: boolean;
  status?: string;
  onShare?: () => void;
  onEdit?: () => void;
  // All asset fields
  asset_name?: string | null;
  asset_description?: string | null;
  asset_contact?: string | null;
  asset_price?: number | null;
  category?: string | null;
  code?: string | null;
  internal_asset_id?: string | null;
  location?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  nfc_id?: string | null;
  notes?: string | null;
  phone?: string | null;
  private_code?: string | null;
  purchase_date?: string | null;
  qr_id?: string | null;
  serial_number?: string | null;
  warranty_expiry?: string | null;
  user_name?: string | null;
  company?: {
    id: number;
    name: string;
    description?: string;
  } | null;
  place?: {
    id: number;
    name: string;
    description?: string;
  } | null;
  images?: string | null;
  documents?: string | null;
  rating?: number | null;
  conditions?: number | null;
}

export default function AssetCard({
  id,
  name,
  imageUrl,
  isShared,
  status,
  onShare,
  onEdit,
  // All asset fields
  asset_name,
  asset_description,
  asset_contact,
  asset_price,
  category,
  code,
  internal_asset_id,
  location,
  latitude,
  longitude,
  nfc_id,
  notes,
  phone,
  private_code,
  purchase_date,
  qr_id,
  serial_number,
  warranty_expiry,
  user_name,
  // Add company and place objects
  company,
  place,
  images,
  documents,
  rating,
  conditions,
}: AssetCardProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [previewState, setPreviewState] = useState<{ isOpen: boolean; type: string; url: string; title: string } | null>(null);
  const navigate = useNavigate();

  // Fetch records only when expanded
  const { data: records, refetch } = useQuery({
    queryKey: ['assetRecords', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('asset_records')
        .select('*')
        .eq('asset_id', id)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      return data;
    },
    enabled: isExpanded, // Only fetch when expanded
  });

  const handleRecordSuccess = () => {
    refetch();
  };

  const handleReminderSuccess = () => {
    refetch();
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div 
      className="group hover:shadow-apple-hover transition-all duration-200 bg-white rounded-apple p-4 border border-apple-light-gray/50 shadow-lg"
      onClick={(e) => {
        e.preventDefault();
        setIsExpanded(!isExpanded);
      }}
    >
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          {/* Thumbnail */}
          <div className="flex-shrink-0">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={name}
                className="h-16 w-16 object-cover rounded-apple"
              />
            ) : (
              <div className="h-16 w-16 bg-apple-light-gray flex items-center justify-center rounded-apple">
                <span className="text-xs text-apple-gray">{t('assetCard.noImage')}</span>
              </div>
            )}
          </div>
          
          {/* Content */}
          <div className="flex-grow min-w-0">
            {/* Title and Details */}
            <div className="truncate">
              <h3 className="font-medium text-apple-dark truncate">{name}</h3>
              {category && (
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-xs bg-white/50 text-apple-dark border-apple-light-gray/50">
                    {(() => {
                      const [mainCategory, subCategory] = category.split('.');
                      if (subCategory) {
                        return t(`forms.asset.categories.${mainCategory}.${subCategory}`);
                      }
                      return category;
                    })()}
                  </Badge>
                </div>
              )}
              <div className="flex flex-col gap-1 mt-1">
                {(company?.name || place?.name) && (
                  <div className="flex items-center gap-2 text-sm text-apple-gray">
                    <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
                    <span className="truncate">
                      {company?.name && place?.name ? `${company.name} - ${place.name}` : company?.name || place?.name}
                    </span>
                  </div>
                )}
                {location && (
                  <div className="flex items-center text-sm text-apple-gray">
                    <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
                    <span className="truncate">{location}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Status Badges - In a new row */}
            {status && (
              <div className="mt-3 flex flex-wrap gap-2">
                <Badge 
                  variant="outline" 
                  className={cn("text-xs", getStatusColor(status))}
                >
                  {status}
                </Badge>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <AppleButton
            variant="primary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/assets/${id}`);
            }}
            className="h-8"
          >
            <Eye className="h-3 w-3 mr-1" />
            {t('assetCard.actions.view')}
          </AppleButton>
        </div>

        {/* Expand/Collapse Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="w-full flex items-center justify-center text-apple-gray hover:text-apple-dark transition-colors"
        >
          {isExpanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>

        {/* Expanded Details */}
        {isExpanded && (
          <div 
            className="pt-2 border-t border-gray-100 space-y-6 text-sm bg-gray-50/80 p-4 rounded-b-apple"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Recent Records Accordion */}
            <AssetRecordsList 
              assetId={id} 
              onRecordAdded={handleRecordSuccess}
              onReminderAdded={handleReminderSuccess}
              onExpand={() => setIsExpanded(true)}
              defaultExpanded={isExpanded}
            />

            {/* Basic Information */}
            <div className="space-y-2 bg-white p-4 rounded-apple shadow-md hover:shadow-lg transition-shadow duration-200">
              <h4 className="font-medium text-apple-dark">{t('assetCard.sections.basicInfo')}</h4>
              <div className="grid gap-2 max-w-[400px]">
                {asset_name && (
                  <div className="grid grid-cols-[120px,1fr] gap-2 items-center">
                    <span className="text-apple-gray">{t('assetCard.fields.assetName')}:</span>
                    <span className="text-apple-dark">{asset_name}</span>
                  </div>
                )}
                {asset_description && (
                  <div className="grid grid-cols-[120px,1fr] gap-2 items-start">
                    <span className="text-apple-gray">{t('assetCard.fields.description')}:</span>
                    <span className="text-apple-dark">{asset_description}</span>
                  </div>
                )}
                {category && (
                  <div className="grid grid-cols-[120px,1fr] gap-2 items-center">
                    <span className="text-apple-gray">{t('assetCard.fields.category')}:</span>
                    <span className="text-apple-dark">{category}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Location Information */}
            <div className="space-y-2 bg-white p-4 rounded-apple shadow-md hover:shadow-lg transition-shadow duration-200">
              <h4 className="font-medium text-apple-dark">{t('assetCard.sections.location')}</h4>
              <div className="grid gap-2 max-w-[400px]">
                {location && (
                  <div className="grid grid-cols-[120px,1fr] gap-2 items-center">
                    <span className="text-apple-gray">{t('assetCard.fields.location')}:</span>
                    <span className="text-apple-dark">{location}</span>
                  </div>
                )}
                {(latitude !== null && longitude !== null) && (
                  <div className="grid grid-cols-[120px,1fr] gap-2 items-center">
                    <span className="text-apple-gray">{t('assetCard.fields.coordinates')}:</span>
                    <span className="text-apple-dark">
                      {latitude.toFixed(6)}, {longitude.toFixed(6)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-2 bg-white p-4 rounded-apple shadow-md hover:shadow-lg transition-shadow duration-200">
              <h4 className="font-medium text-apple-dark">{t('assetCard.sections.contactInfo')}</h4>
              <div className="grid gap-2 max-w-[400px]">
                {asset_contact && (
                  <div className="grid grid-cols-[120px,1fr] gap-2 items-center">
                    <span className="text-apple-gray">{t('assetCard.fields.contact')}:</span>
                    <span className="text-apple-dark">{asset_contact}</span>
                  </div>
                )}
                {phone && (
                  <div className="grid grid-cols-[120px,1fr] gap-2 items-center">
                    <span className="text-apple-gray">{t('assetCard.fields.phone')}:</span>
                    <span className="text-apple-dark">{phone}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Asset Details */}
            <div className="space-y-2 bg-white p-4 rounded-apple shadow-md hover:shadow-lg transition-shadow duration-200">
              <h4 className="font-medium text-apple-dark">{t('assetCard.sections.assetDetails')}</h4>
              <div className="grid gap-2 max-w-[400px]">
                {internal_asset_id && (
                  <div className="grid grid-cols-[120px,1fr] gap-2 items-center">
                    <span className="text-apple-gray">{t('assetCard.fields.internalId')}:</span>
                    <span className="text-apple-dark">{internal_asset_id}</span>
                  </div>
                )}
                {serial_number && (
                  <div className="grid grid-cols-[120px,1fr] gap-2 items-center">
                    <span className="text-apple-gray">{t('assetCard.fields.serialNo')}:</span>
                    <span className="text-apple-dark">{serial_number}</span>
                  </div>
                )}
                {nfc_id && (
                  <div className="grid grid-cols-[120px,1fr] gap-2 items-center">
                    <span className="text-apple-gray">{t('assetCard.fields.nfcId')}:</span>
                    <span className="text-apple-dark">{nfc_id}</span>
                  </div>
                )}
                {qr_id && (
                  <div className="grid grid-cols-[120px,1fr] gap-2 items-center">
                    <span className="text-apple-gray">{t('assetCard.fields.qrId')}:</span>
                    <span className="text-apple-dark">{qr_id}</span>
                  </div>
                )}
                {code && (
                  <div className="grid grid-cols-[120px,1fr] gap-2 items-center">
                    <span className="text-apple-gray">{t('assetCard.fields.code')}:</span>
                    <span className="text-apple-dark">{code}</span>
                  </div>
                )}
                {asset_price && (
                  <div className="grid grid-cols-[120px,1fr] gap-2 items-center">
                    <span className="text-apple-gray">{t('assetCard.fields.price')}:</span>
                    <span className="text-apple-dark">${asset_price.toLocaleString()}</span>
                  </div>
                )}
                {purchase_date && (
                  <div className="grid grid-cols-[120px,1fr] gap-2 items-center">
                    <span className="text-apple-gray">{t('assetCard.fields.purchase')}:</span>
                    <span className="text-apple-dark">{formatDate(purchase_date)}</span>
                  </div>
                )}
                {warranty_expiry && (
                  <div className="grid grid-cols-[120px,1fr] gap-2 items-center">
                    <span className="text-apple-gray">{t('assetCard.fields.warranty')}:</span>
                    <span className="text-apple-dark">{formatDate(warranty_expiry)}</span>
                  </div>
                )}
                {rating !== null && rating !== undefined && (
                  <div className="grid grid-cols-[120px,1fr] gap-2 items-center">
                    <span className="text-apple-gray">{t('forms.asset.rating')}:</span>
                    <span className="text-apple-dark">{rating}/5</span>
                  </div>
                )}
                {conditions !== null && conditions !== undefined && (
                  <div className="grid grid-cols-[120px,1fr] gap-2 items-center">
                    <span className="text-apple-gray">{t('forms.asset.conditions')}:</span>
                    <span className="text-apple-dark">{conditions}/5</span>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Information */}
            <div className="space-y-2 bg-white p-4 rounded-apple shadow-md hover:shadow-lg transition-shadow duration-200">
              <h4 className="font-medium text-apple-dark">{t('assetCard.sections.additionalInfo')}</h4>
              <div className="grid gap-2 max-w-[400px]">
                {notes && (
                  <div className="grid grid-cols-[120px,1fr] gap-2 items-start">
                    <span className="text-apple-gray">{t('assetCard.fields.notes')}:</span>
                    <span className="text-apple-dark">{notes}</span>
                  </div>
                )}
                {user_name && (
                  <div className="grid grid-cols-[120px,1fr] gap-2 items-center">
                    <span className="text-apple-gray">{t('assetCard.fields.userName')}:</span>
                    <span className="text-apple-dark">{user_name}</span>
                  </div>
                )}
                {private_code && (
                  <div className="grid grid-cols-[120px,1fr] gap-2 items-center">
                    <span className="text-apple-gray">{t('assetCard.fields.privateCode')}:</span>
                    <span className="text-apple-dark">{private_code}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Attachments */}
            {(images?.length > 0 || documents?.length > 0) && (
              <div className="space-y-2 bg-white p-4 rounded-apple shadow-md hover:shadow-lg transition-shadow duration-200">
                <h4 className="font-medium text-apple-dark">{t('assetCard.sections.attachments')}</h4>
                <div className="space-y-4">
                  {/* Images */}
                  {images && images.split(',').filter(Boolean).length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-apple-gray">
                        <ImageIcon className="h-4 w-4" />
                        <span>{t('records.images')} ({images.split(',').filter(Boolean).length})</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {images.split(',').filter(Boolean).map((image, index) => (
                          <button
                            key={index}
                            onClick={() => setPreviewState({
                              isOpen: true,
                              type: 'image',
                              url: image,
                              title: `${t('records.image')} ${index + 1}`
                            })}
                            className="w-16 h-16 rounded-md overflow-hidden border border-apple-light-gray/50 hover:border-apple-blue transition-colors"
                          >
                            <img
                              src={image}
                              alt={`${t('records.preview')} ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Documents */}
                  {documents && documents.split(',').filter(Boolean).length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-apple-gray">
                        <FileText className="h-4 w-4" />
                        <span>{t('records.documents')} ({documents.split(',').filter(Boolean).length})</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {documents.split(',').filter(Boolean).map((doc, index) => (
                          <button
                            key={index}
                            onClick={() => setPreviewState({
                              isOpen: true,
                              type: 'document',
                              url: doc,
                              title: `${t('records.document')} ${index + 1}`
                            })}
                            className="flex items-center gap-2 px-3 py-2 rounded-md border border-apple-light-gray/50 hover:border-apple-blue transition-colors text-sm text-apple-gray hover:text-apple-blue"
                          >
                            <FileText className="h-4 w-4" />
                            <span>{t('records.doc')} {index + 1}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Preview Dialog */}
      <Dialog open={previewState?.isOpen} onOpenChange={(open) => !open && setPreviewState(null)}>
        <DialogContent className="sm:max-w-[800px] h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{previewState?.title}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto flex items-center justify-center p-4">
            {previewState?.type === 'image' ? (
              <img
                src={previewState.url}
                alt={previewState.title}
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <iframe
                src={previewState?.url}
                title={previewState?.title}
                className="w-full h-full"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}